import { useI18n } from "@core/i18n/i18n";
import { templateOperationsManager } from "@core/service-container";

import { sendAnalyticsEventDeleteTemplate } from "@domain/template/template-analytics";
import { ToastContext } from "@ui/components/common/toast-provider";
import { ConfirmActionDialog } from "@ui/pages/program-builder/dialog/confirm-dialog";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext, useState } from "react";

export interface DeleteTemplateDialogProps extends DialogProps {
	templateId: string;
	templateName: string;
	onSuccess?: () => void;
}

export const DeleteTemplateDialog = ({
	templateId,
	templateName,
	onSuccess,
	...otherProps
}: DeleteTemplateDialogProps): JSX.Element => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);

	const [awaitingDelete, setAwaitingDelete] = useState(false);

	const deleteTemplate = useCallback(async () => {
		setAwaitingDelete(true);
		try {
			await templateOperationsManager.performDeleteOperation(templateId, templateName);
			setAwaitingDelete(false);
			sendAnalyticsEventDeleteTemplate(templateId);
			showToast(
				format("global.deleteTemplateOperation.successToast.title"),
				format("global.deleteTemplateOperation.successToast.message", { name: templateName }),
				"success",
			);
			otherProps.onHide();
			onSuccess?.();
		} catch (error) {
			console.error("Failed to delete template", error);
			setAwaitingDelete(false);
			showToast(
				format("global.deleteTemplateOperation.errorToast.title"),
				format("global.deleteTemplateOperation.errorToast.message"),
			);
		}
	}, [format, onSuccess, otherProps, showToast, templateId, templateName]);

	return (
		<ConfirmActionDialog
			{...otherProps}
			onCancel={otherProps.onHide}
			onConfirm={deleteTemplate}
			confirmLoading={awaitingDelete}
			title={format("deleteTemplateDialog.title")}
			message={format("deleteTemplateDialog.message")}
		/>
	);
};
